import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { siteStage } from '../../config/siteStage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TENURES = [
  { value: 'private', label: 'Private renter' },
  { value: 'social', label: 'Social renter (housing association / council)' },
  { value: 'shared_owner', label: 'Shared owner' },
  { value: 'leaseholder', label: 'Leaseholder' },
];

const ISSUE_CATEGORIES = [
  'Damp, mould or condensation',
  'Repairs & maintenance',
  'Safety concern',
  'Complaint or communication issue',
  'Service charges',
  'Other',
];

const SHARE_OPTIONS = [
  'Landlord',
  'Housing officer',
  'Letting / managing agent',
  'Advice worker',
  'Representative',
  'Not sure yet',
];

const SUPPORT_INTERESTS = [
  'Using ResiAX / product help',
  'Organising housing history & evidence',
  'Preparing for a landlord or provider conversation',
  'Resident association / representative support',
  'Legal support',
  'Surveying / property advice',
  'Advocacy / welfare support',
  'Other',
];

const PilotApplicationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tenure: '',
    issueCategory: '',
    issueStatus: '',
    description: '',
    shareWith: [],
    supportInterests: [],
    cohortInterest: '',
    updatesConsent: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const [error, setError] = useState('');
  const successRef = useRef(null);

  // The success card is much shorter than the form, so the viewport can land
  // on the footer after submit — bring the confirmation into view.
  useEffect(() => {
    if (isSuccess) {
      successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSuccess]);

  // Issue-category hint from scenario CTAs (non-sensitive, via sessionStorage)
  React.useEffect(() => {
    const stored = sessionStorage.getItem('resiax_issue_category');
    if (stored && ISSUE_CATEGORIES.includes(stored)) {
      setFormData((prev) => ({ ...prev, issueCategory: stored }));
      sessionStorage.removeItem('resiax_issue_category');
    }
    const handler = (e) => {
      const cat = e?.detail?.issueCategory;
      if (cat && ISSUE_CATEGORIES.includes(cat)) {
        setFormData((prev) => ({ ...prev, issueCategory: cat }));
      }
    };
    window.addEventListener('resiax:set-issue-category', handler);
    return () => window.removeEventListener('resiax:set-issue-category', handler);
  }, []);

  const toggleShare = (option) => {
    setFormData((prev) => ({
      ...prev,
      shareWith: prev.shareWith.includes(option)
        ? prev.shareWith.filter((o) => o !== option)
        : [...prev.shareWith, option],
    }));
  };

  const toggleSupportInterest = (option) => {
    setFormData((prev) => ({
      ...prev,
      supportInterests: prev.supportInterests.includes(option)
        ? prev.supportInterests.filter((o) => o !== option)
        : [...prev.supportInterests, option],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const details = [
      `Issue category: ${formData.issueCategory}`,
      `Issue status: ${formData.issueStatus === 'current' ? 'Current' : 'Recent (within the last few months)'}`,
      formData.shareWith.length ? `May share with: ${formData.shareWith.join(', ')}` : null,
      formData.description ? `\nDescription: ${formData.description}` : null,
    ].filter(Boolean).join('\n');

    try {
      const response = await fetch(`${BACKEND_URL}/api/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          user_type: 'resident',
          tenant_type: formData.tenure,
          pain_points: details,
          pain_points_selected: [],
          desired_features: '',
          subscribe_blog: formData.updatesConsent,
          interest_type: 'resident_early_access_pilot',
          supported_cohort_interest: formData.cohortInterest || null,
          support_interest: formData.supportInterests.length ? formData.supportInterests.join(', ') : null,
          source_page: (typeof window !== 'undefined' ? window.location.pathname : '/'),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to submit');
      }
      const result = await response.json();
      localStorage.setItem('resiax_access', 'granted');
      setHandoff({
        handoffToken: result?.handoff_token || null,
      });
      setIsSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const continueToApp = () => {
    // Secure handoff: opaque single-use token only (no personal data in the URL).
    if (siteStage.opaqueHandoffEnabled && handoff?.handoffToken) {
      const url = new URL(siteStage.pilotStartUrl);
      url.searchParams.set('token', handoff.handoffToken);
      window.location.href = url.toString();
      return;
    }
    window.location.href = siteStage.appUrl;
  };

  if (isSuccess) {
    return (
      <div ref={successRef} data-testid="pilot-register-success" className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 text-center space-y-5">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-[#1F1E1E] mb-2">You&apos;re registered for ResiAX early access</h3>
          <p className="text-[#3B3535]/70 max-w-md mx-auto">
            You can continue to create your account and explore the platform. We&apos;ll contact
            you separately to confirm whether you&apos;re joining the supported eight-week pilot cohort.
          </p>
        </div>
        <Button
          data-testid="continue-to-resiax-btn"
          onClick={continueToApp}
          className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
        >
          Continue to ResiAX
          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100 space-y-5" data-testid="pilot-register-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="pilot-name" className="text-[#3B3535] font-medium">Name <span className="text-[#B5541B]">*</span></Label>
          <Input
            id="pilot-name"
            data-testid="pilot-apply-name-input"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Your full name"
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pilot-email" className="text-[#3B3535] font-medium">Email <span className="text-[#B5541B]">*</span></Label>
          <Input
            id="pilot-email"
            data-testid="pilot-apply-email-input"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="you@example.com"
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#3B3535] font-medium">Your tenure <span className="text-[#B5541B]">*</span></Label>
          <Select required value={formData.tenure} onValueChange={(v) => setFormData({ ...formData, tenure: v })}>
            <SelectTrigger data-testid="pilot-apply-tenure-select" className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]">
              <SelectValue placeholder="Select your tenure" />
            </SelectTrigger>
            <SelectContent>
              {TENURES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#3B3535] font-medium">Type of issue <span className="text-[#B5541B]">*</span></Label>
          <Select required value={formData.issueCategory} onValueChange={(v) => setFormData({ ...formData, issueCategory: v })}>
            <SelectTrigger data-testid="pilot-apply-issue-select" className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]">
              <SelectValue placeholder="Select the closest match" />
            </SelectTrigger>
            <SelectContent>
              {ISSUE_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-[#3B3535] font-medium text-sm">Is the issue current or recent? <span className="text-[#B5541B]">*</span></legend>
        <div className="flex flex-wrap gap-3">
          {[
            { value: 'current', label: 'Current — still ongoing' },
            { value: 'recent', label: 'Recent — within the last few months' },
          ].map((opt) => (
            <label
              key={opt.value}
              data-testid={`pilot-apply-status-${opt.value}`}
              className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                formData.issueStatus === opt.value
                  ? 'border-[#B5541B] bg-[#B5541B]/10 text-[#B5541B] font-semibold'
                  : 'border-gray-200 text-[#3B3535]/80 hover:border-[#B5541B]/40'
              }`}
            >
              <input
                type="radio"
                name="issueStatus"
                value={opt.value}
                required
                checked={formData.issueStatus === opt.value}
                onChange={() => setFormData({ ...formData, issueStatus: opt.value })}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="space-y-2">
        <Label htmlFor="pilot-description" className="text-[#3B3535] font-medium">
          Tell us a little about the issue <span className="text-[#3B3535]/50">(optional)</span>
        </Label>
        <Textarea
          id="pilot-description"
          data-testid="pilot-apply-description-input"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="A sentence or two is plenty — you can share more once you're set up."
          rows={3}
          className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none"
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-[#3B3535] font-medium text-sm">
          Who might you choose to share information with? <span className="text-[#3B3535]/50">(optional)</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {SHARE_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              data-testid={`pilot-apply-share-${option.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              onClick={() => toggleShare(option)}
              aria-pressed={formData.shareWith.includes(option)}
              className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${
                formData.shareWith.includes(option)
                  ? 'border-[#B5541B] bg-[#B5541B] text-white font-semibold'
                  : 'border-gray-200 text-[#3B3535]/80 hover:border-[#B5541B]/40'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="sr-only">Support interest</legend>
        <p className="text-sm font-medium text-[#3B3535]">
          What kind of support would be useful? <span className="text-[#3B3535]/50">(optional — pick any)</span>
        </p>
        <p className="text-xs text-[#3B3535]/60 leading-relaxed">
          You don&apos;t need to know exactly what help you need — this just tells us where support matters most.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {SUPPORT_INTERESTS.map((option) => (
            <button
              key={option}
              type="button"
              data-testid={`pilot-support-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              onClick={() => toggleSupportInterest(option)}
              aria-pressed={formData.supportInterests.includes(option)}
              className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${
                formData.supportInterests.includes(option)
                  ? 'border-[#B5541B] bg-[#B5541B] text-white font-semibold'
                  : 'border-gray-200 text-[#3B3535]/80 hover:border-[#B5541B]/40'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2 bg-[#B5541B]/5 rounded-xl p-4">
        <legend className="sr-only">Supported cohort interest</legend>
        <p className="text-sm font-medium text-[#3B3535]">
          Interested in the supported eight-week cohort? <span className="text-[#3B3535]/50">(optional)</span>
        </p>
        <p className="text-xs text-[#3B3535]/60 leading-relaxed">
          A smaller group gets founder-led onboarding and short feedback check-ins. Places are limited and confirmed separately.
        </p>
        <div className="flex gap-3 pt-1">
          {[
            { value: 'yes', label: 'Yes, I\u2019m interested' },
            { value: 'no', label: 'Not right now' },
          ].map((opt) => (
            <label
              key={opt.value}
              data-testid={`pilot-cohort-${opt.value}`}
              className={`flex items-center gap-2 text-sm px-4 py-2 rounded-xl border cursor-pointer transition-colors ${
                formData.cohortInterest === opt.value
                  ? 'border-[#B5541B] bg-white text-[#B5541B] font-semibold'
                  : 'border-gray-200 bg-white text-[#3B3535]/80 hover:border-[#B5541B]/40'
              }`}
            >
              <input
                type="radio"
                name="cohortInterest"
                value={opt.value}
                checked={formData.cohortInterest === opt.value}
                onChange={() => setFormData({ ...formData, cohortInterest: opt.value })}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex items-start space-x-3">
        <Checkbox
          id="pilot-updates-consent"
          data-testid="pilot-updates-consent-checkbox"
          checked={formData.updatesConsent}
          onCheckedChange={(checked) => setFormData({ ...formData, updatesConsent: !!checked })}
          className="mt-0.5 border-[#B5541B] data-[state=checked]:bg-[#B5541B] data-[state=checked]:border-[#B5541B]"
        />
        <Label htmlFor="pilot-updates-consent" className="text-sm text-[#3B3535] leading-relaxed cursor-pointer font-normal">
          Keep me updated about early access and the pilot by email.
        </Label>
      </div>

      {error && (
        <div data-testid="pilot-apply-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" role="alert">{error}</div>
      )}

      <Button
        type="submit"
        data-testid="pilot-apply-submit-btn"
        disabled={isSubmitting}
        className="w-full bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 w-5 h-5 animate-spin" />
            Registering...
          </>
        ) : (
          'Register for the resident early access pilot'
        )}
      </Button>

      <p className="text-xs text-[#3B3535]/60 leading-relaxed text-center">
        Your information is confidential. It will not be shared with your landlord without your
        permission. We use it to support your participation and improve ResiAX. See our{' '}
        <Link to="/privacy" data-testid="pilot-form-privacy-link" className="font-semibold text-[#B5541B] hover:text-[#8B3F14]">Privacy Policy</Link>.
      </p>
    </form>
  );
};

export default PilotApplicationForm;
